import React, { useState } from 'react';
import { Typography, Stack, Modal, Box, Card, CardContent, CardMedia, ButtonBase, IconButton, Snackbar, Alert } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InviteResidentIcon from '../../icons/InviteResidentIcon.svg';
import SendEmailIcon from '../../icons/SendEmailIcon.svg';
import { makeStyles } from '@mui/styles';
import SpinnerModal from '../../shared/Modal/SpinnerModal';
import InvitationEmail from '../../icons/InvitationEmail.svg';
import IssueSpecialCode from '../../icons/IssueSpecialCode.svg';
import back2 from '../../icons/back2.svg';
import close2 from '../../icons/close2.svg';
import { Role } from '@inosio/payments-sdk';
import { InvitationOptions, SubjectDetailsInvitation } from './hooks/useRegInviteConfigs';

const useStyles = makeStyles((theme) => ({
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  cardContainer: {
    width: '100% !important',
  },
}));

export const enum RegInviteModalStates {
  InviteToPortal = 'Invite to Portal',
  InvitationEmail = 'Invitation Email',
  SpecialCode = 'Special Code',
  ActivationLink = 'Activation Link',
  InvitationLink = 'Invitation Link', //Getting the invitation link without sending the email
}

export interface RegistrationInviteModalProps {
  open: boolean;
  handleClose: () => void;
  subjectDetails: SubjectDetailsInvitation;
  specialCode: string;
  activationLink: string;
  invitationError: string;
  setInvitationError: (value: React.SetStateAction<string>) => void;
  setIsRISpinnerModalOpen: (value: React.SetStateAction<boolean>) => void;
  handleGetInvitation: (invitationType: InvitationOptions, uuid: string, email: string, firstName: string, lastName: string, middleName?: string, updateTokenId?: string) => void;
  isRISpinnerModalOpen: boolean;
  rISpinnerStatus: string | null;
  resetInviteResults: () => void;
  modalState: RegInviteModalStates;
  setModalState: (value: React.SetStateAction<RegInviteModalStates>) => void;
}

const RegInviteModal = (props: RegistrationInviteModalProps) => {
  const classes = useStyles();

  const goToPrevState = () => {
    if ([RegInviteModalStates.InvitationEmail, RegInviteModalStates.SpecialCode, RegInviteModalStates.InvitationLink].includes(props.modalState)) {
      props.setModalState(RegInviteModalStates.InviteToPortal);
    } else if (props.modalState === RegInviteModalStates.ActivationLink) {
      props.setModalState(RegInviteModalStates.InvitationEmail);
    }
  };

  const [email, setEmail] = useState(props?.subjectDetails?.Email);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const handleCloseSpinnerModal = () => {
    props.setIsRISpinnerModalOpen(false);
  };

  return (
    <div>
      <Modal
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            background: '#FFFFFF',
            boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
            borderRadius: '24px',
            p: 4,
            outline: 'none',
          }}
        >
          <Stack direction="column" justifyContent="start" alignItems="space-between" spacing={1}>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
              {![RegInviteModalStates.InviteToPortal, RegInviteModalStates.SpecialCode, RegInviteModalStates.ActivationLink, RegInviteModalStates.InvitationLink].includes(props.modalState) && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="go to prev"
                  onClick={goToPrevState}
                  sx={{
                    position: 'absolute',
                    left: 0,
                  }}
                >
                  <img src={back2} alt="back" />
                </IconButton>
              )}
              {props.modalState !== RegInviteModalStates.InviteToPortal && (
                <Typography
                  sx={{
                    fontFamily: '"Work Sans",Sans serif',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '22px',
                    color: '#14142B',
                    marginLeft: '0 !important',
                  }}
                >
                  {props.modalState}
                </Typography>
              )}
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Close modal"
                onClick={props.handleClose}
                sx={{
                  position: 'absolute',
                  right: 0,
                }}
              >
                <img src={close2} alt="close" />
              </IconButton>
            </Stack>
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{
                minHeight: 470,
              }}
            >
              {props.modalState === RegInviteModalStates.InviteToPortal ? (
                <React.Fragment>
                  {props.invitationError?.length ? (
                    <div className={classes.globalError}>
                      <Typography
                        sx={{
                          fontFamily: "'Open Sans', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '18px',
                          color: '#DB3939',
                        }}
                      >
                        {props.invitationError}
                      </Typography>
                    </div>
                  ) : (
                    ''
                  )}
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '21px',
                      textAlign: 'center',
                      color: '#14142B',
                      width: '240px',
                    }}
                  >
                    Choose how you want to invite the user:
                  </Typography>
                  <Card
                    sx={{
                      padding: '0',
                      width: 340,
                      boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
                      borderRadius: '20px',
                      marginTop: '50px !important',
                    }}
                  >
                    <ButtonBase
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        width: '100%',
                        padding: '15px 22px',
                        textAlign: 'initial',
                      }}
                      onClick={() => {
                        props.setInvitationError('');
                        props.resetInviteResults();
                        props.handleGetInvitation(
                          InvitationOptions.GetActivationLink,
                          props.subjectDetails.ProfileUuid,
                          email,
                          props.subjectDetails.FirstName,
                          props.subjectDetails.LastName,
                          props.subjectDetails.MiddleName,
                          props.subjectDetails.UpdateTokenId
                        );
                      }}
                      id={'invitation-email'}
                    >
                      <CardMedia component="img" sx={{ width: 67, marginRight: '15px' }} image={InvitationEmail} alt="Invitation Email" />
                      <CardContent>
                        <Typography
                          sx={{
                            fontFamily: 'Work Sans',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            fontSize: '18px',
                            lineHeight: '23px',
                            color: '#14142B',
                          }}
                        >
                          Get Activation Link
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Work Sans',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '14px',
                            color: '#14142B',
                          }}
                        >
                          Give link to the user yourself.
                        </Typography>
                      </CardContent>
                    </ButtonBase>
                  </Card>
                  <Card
                    sx={{
                      padding: '0',
                      width: 340,
                      boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
                      borderRadius: '20px',
                      marginTop: '50px !important',
                    }}
                  >
                    <ButtonBase
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        width: '100%',
                        padding: '15px 22px',
                        textAlign: 'initial',
                      }}
                      onClick={() => {
                        props.setInvitationError('');
                        props.resetInviteResults();
                        props.setModalState(RegInviteModalStates.InvitationEmail);
                      }}
                      id={'invitation-email'}
                    >
                      <CardMedia component="img" sx={{ width: 67, marginRight: '15px' }} image={InvitationEmail} alt="Invitation Email" />
                      <CardContent>
                        <Typography
                          sx={{
                            fontFamily: 'Work Sans',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            fontSize: '18px',
                            lineHeight: '23px',
                            color: '#14142B',
                          }}
                        >
                          Invitation Email
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Work Sans',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '14px',
                            color: '#14142B',
                          }}
                        >
                          Emails an activation link.
                        </Typography>
                      </CardContent>
                    </ButtonBase>
                  </Card>
                  {props.subjectDetails.RoleType !== Role.Applicant && (
                    <Card
                      sx={{
                        padding: '0',
                        width: 340,
                        boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
                        borderRadius: '20px',
                        marginTop: '50px !important',
                      }}
                    >
                      <ButtonBase
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start',
                          width: '100%',
                          padding: '15px 0 15px 18px',
                          textAlign: 'initial',
                        }}
                        onClick={() =>
                          props.handleGetInvitation(
                            InvitationOptions.GetShortCode,
                            props.subjectDetails.ProfileUuid,
                            email,
                            props.subjectDetails.FirstName,
                            props.subjectDetails.LastName,
                            props.subjectDetails.MiddleName,
                            props.subjectDetails.UpdateTokenId
                          )
                        }
                        id={'special-code'}
                      >
                        <CardMedia component="img" sx={{ width: 78, marginRight: '8px' }} image={IssueSpecialCode} alt="Invitation Email" />
                        <CardContent>
                          <Typography
                            sx={{
                              fontFamily: 'Work Sans',
                              fontStyle: 'normal',
                              fontWeight: 700,
                              fontSize: '18px',
                              lineHeight: '23px',
                              color: '#14142B',
                            }}
                          >
                            Issue Special Code
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Work Sans',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              fontSize: '12px',
                              lineHeight: '14px',
                              color: '#14142B',
                            }}
                          >
                            Use if email delivery is a problem.
                          </Typography>
                        </CardContent>
                      </ButtonBase>
                    </Card>
                  )}
                </React.Fragment>
              ) : props.modalState === RegInviteModalStates.InvitationEmail ? (
                <React.Fragment>
                  {props.invitationError?.length ? (
                    <div className={classes.globalError}>
                      <Typography
                        sx={{
                          fontFamily: "'Open Sans', sans-serif",
                          fontStyle: 'normal',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '18px',
                          color: '#DB3939',
                        }}
                      >
                        {props.invitationError}
                      </Typography>
                    </div>
                  ) : (
                    ''
                  )}
                  <img src={InviteResidentIcon} alt="" />
                  <Typography
                    sx={{
                      fontFamily: '"Work Sans",Sans serif',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '25px',
                      textAlign: 'center',
                      color: '#14142B',
                    }}
                  >
                    Do you want to send a link to {`${props?.subjectDetails?.FirstName || ''}`} {`${props?.subjectDetails?.MiddleName || ''}`} {`${props?.subjectDetails?.LastName || ''}`}
                  </Typography>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="text"
                      style={{
                        background: '#FFF',
                        borderRadius: '24px',
                        height: '57px',
                        width: '318px',
                        padding: '17px',
                        border: '1px solid #F1F0F5',
                        outline: 'none',
                        zIndex: 1,
                        fontFamily: '"Work Sans",Sans serif',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '22px',
                      }}
                      placeholder="Email"
                      value={email}
                      onChange={(e: any) => setEmail(e.target.value)}
                      id="activation-link-recipient"
                    />
                    <div
                      style={{
                        background: email.length === 0 ? '#D3D3D3' : '#39A2DB',
                        borderRadius: '24px',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: 62,
                        height: 57,
                        pointerEvents: email.length === 0 ? 'none' : 'auto',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        props.handleGetInvitation(
                          InvitationOptions.SendActivationLink,
                          props.subjectDetails.ProfileUuid,
                          email,
                          props.subjectDetails.FirstName,
                          props.subjectDetails.LastName,
                          props.subjectDetails.MiddleName,
                          props.subjectDetails.UpdateTokenId
                        )
                      }
                    >
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <img
                          src={SendEmailIcon}
                          style={{
                            position: 'absolute',
                            top: 20,
                            height: '20px',
                            width: '20px',
                            cursor: 'pointer',
                          }}
                          alt=""
                        />
                      </Stack>
                    </div>
                  </div>
                </React.Fragment>
              ) : props.modalState === RegInviteModalStates.ActivationLink ? (
                <React.Fragment>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '24px',
                      lineHeight: '25px',
                      color: '#14142B',
                      textAlign: 'center',
                      marginTop: '25px !important',
                    }}
                  >
                    Invite has been sent.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      textAlign: 'center',
                      marginTop: '28px !important',
                    }}
                  >
                    It may take a few minutes before the user receives it.
                    <br />
                    <br />
                    but you can also send this link to them from your own email.
                  </Typography>
                  <Box
                    sx={{
                      background: '#F1F0F5',
                      borderRadius: '24px',
                      width: 318,
                      padding: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      marginTop: '25px !important',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        lineHeight: '22px',
                        color: '#14142B',
                        marginRight: '25px',
                      }}
                      data-activation-link={props.activationLink} //for e2e tests
                    >
                      {`Click to copy activation link`}
                    </Typography>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="Copy special code"
                      onClick={() => {
                        navigator.clipboard.writeText(props.activationLink);
                        setSnackBarOpen(true);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 5,
                      }}
                      id="copy-activation-link"
                    >
                      <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                    </IconButton>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      textAlign: 'center',
                      marginTop: '15px !important',
                    }}
                  >
                    Note: Provide this link to the user so they can register.
                  </Typography>
                </React.Fragment>
              ) : props.modalState === RegInviteModalStates.InvitationLink ? (
                <React.Fragment>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      textAlign: 'center',
                    }}
                  >
                    Invite Link Generated
                  </Typography>
                  <Box
                    sx={{
                      background: '#F1F0F5',
                      borderRadius: '24px',
                      width: 318,
                      padding: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      marginTop: '25px !important',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        lineHeight: '22px',
                        color: '#14142B',
                        marginRight: '25px',
                      }}
                      data-activation-link={props.activationLink} //for e2e tests
                    >
                      {`Click to copy invitation link`}
                    </Typography>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="Copy special code"
                      onClick={() => {
                        navigator.clipboard.writeText(props.activationLink);
                        setSnackBarOpen(true);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 5,
                      }}
                      id="copy-activation-link"
                    >
                      <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                    </IconButton>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      textAlign: 'center',
                      marginTop: '15px !important',
                    }}
                  >
                    Note: Provide this link to the user so they can register.
                  </Typography>
                </React.Fragment>
              ) : props.modalState === RegInviteModalStates.SpecialCode ? (
                <React.Fragment>
                  <Box
                    sx={{
                      background: '#F1F0F5',
                      borderRadius: '24px',
                      width: 318,
                      padding: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      marginTop: '25px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        lineHeight: '22px',
                        color: '#14142B',
                      }}
                      data-special-code={props.specialCode} //for e2e tests
                    >
                      {props.specialCode}
                    </Typography>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="Copy special code"
                      onClick={() => {
                        navigator.clipboard.writeText(props.specialCode);
                        setSnackBarOpen(true);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 5,
                      }}
                      id="copy-special-code"
                    >
                      <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                    </IconButton>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      width: 379,
                      textAlign: 'center',
                      marginTop: '22px !important',
                    }}
                  >
                    {props.subjectDetails.FirstName} can use this code to register at Inosio.com without receivng an activation link via email.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '18px',
                      lineHeight: '25px',
                      color: '#14142B',
                      width: 350,
                      textAlign: 'center',
                      marginTop: '25px !important',
                    }}
                  >
                    Next steps:
                    <br />
                    <br />
                    1.Provide this code to {`${props.subjectDetails.FirstName || ''} ${props.subjectDetails.LastName || ''}`}
                    <br />
                    2.Have {props.subjectDetails.FirstName} navigate to {`https://inosio.com/code`}.<br />
                    3.{props.subjectDetails.FirstName} should enter the code.
                    <br />
                    4.{props.subjectDetails.FirstName} will need to register with an email.
                  </Typography>
                </React.Fragment>
              ) : (
                <></>
              )}
            </Stack>
          </Stack>
        </Box>
      </Modal>
      {props.isRISpinnerModalOpen ? (
        <SpinnerModal
          openSpinnerModal={props.isRISpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={props.rISpinnerStatus}
          closeCancel={() => {}}
          width="520px"
          height="560px"
        />
      ) : (
        <></>
      )}
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarOpen(false);
        }}
      >
        <Alert
          onClose={() => {
            setSnackBarOpen(false);
          }}
          severity="success"
          sx={{ width: '100%' }}
          id="message-copied"
        >
          Copied to Clipboard
        </Alert>
      </Snackbar>
    </div>
  );
};
export default RegInviteModal;
